import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetGalleryEventsQuery,
  useUpdateGalleryEventMutation,
  useDeleteGalleryEventMutation,
} from "./galleryEventsApiSlice";
import { useGetGalleryCategoriesQuery } from "../galleryCategories/galleryCategoriesApiSlice";

const EditGalleryEvent = () => {

  const { id } = useParams();
  const navigate = useNavigate();

  const { data: events = {}, isLoading } =
    useGetGalleryEventsQuery();

  const { data: categories = {} } =
    useGetGalleryCategoriesQuery();

  const event = events?.data?.find((e) => e._id === id);

  // ===============================
  // STATE
  // ===============================

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [location, setLocation] = useState("");
  const [mantra, setMantra] = useState("");
  const [order, setOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(true);

  const [updateEvent, { isLoading: isUpdating, isError, error }] =
    useUpdateGalleryEventMutation();

  const [deleteEvent, { isLoading: isDeleting }] =
    useDeleteGalleryEventMutation();

  // ===============================
  // LOAD EVENT DATA
  // ===============================

  useEffect(() => {

    if (event) {

      setTitle(event.title || "");

      setCategory(
        event.category?._id || event.category || ""
      );

      setDescription(event.description || "");

      setEventDate(
        event.eventDate
          ? event.eventDate.substring(0, 10)
          : ""
      );

      setLocation(event.location || "");

      setMantra(event.mantra || "");

      setOrder(event.order || 0);

      setIsPublished(event.isPublished ?? true);

    }

  }, [event]);

  if (isLoading)
    return <p className="container mt-4">Loading...</p>;

  if (!event)
    return <p className="container mt-4">Event not found</p>;

  // ===============================
  // SUBMIT
  // ===============================

  const onSubmit = async (e) => {

    e.preventDefault();

    try {

      await updateEvent({
  id,
  data: {
    title,
    category,
    description,
    eventDate,
    location,
    mantra,
    order: Number(order),
    isPublished,
  },
}).unwrap();

      navigate("/dashboard/gallery-events");

    } catch (err) {

      console.error(err);

    }

  };

  // ===============================
  // DELETE
  // ===============================

  const handleDelete = async () => {

    if (window.confirm("Are you sure you want to delete this event?")) {

      try {

        await deleteEvent(id).unwrap();

        navigate("/dashboard/gallery-events");

      } catch (err) {

        console.error(err);

      }

    }

  };

  // ===============================
  // UI
  // ===============================

  return (

    <div className="container mt-4">

      <div className="card">

        <div className="card-body">

          <h2 className="mb-4">Edit Gallery Event</h2>

          <form onSubmit={onSubmit}>

            {/* TITLE */}
            <div className="mb-3">

              <label className="form-label">
                Title
              </label>

              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) =>
                  setTitle(e.target.value)
                }
                required
              />

            </div>

            {/* CATEGORY */}
            <div className="mb-3">

              <label className="form-label">
                Category
              </label>

              <select
                className="form-control"
                value={category}
                onChange={(e) =>
                  setCategory(e.target.value)
                }
                required
              >

                <option value="">
                  Select Category
                </option>

                {categories?.data?.map((cat) => (

                  <option
                    key={cat._id}
                    value={cat._id}
                  >

                    {cat.title}

                  </option>

                ))}

              </select>

            </div>

            {/* DATE */}
            <div className="mb-3">

              <label className="form-label">
                Event Date
              </label>

              <input
                type="date"
                className="form-control"
                value={eventDate}
                onChange={(e) =>
                  setEventDate(e.target.value)
                }
              />

            </div>

            {/* LOCATION */}
            <div className="mb-3">

              <label className="form-label">
                Location
              </label>

              <input
                type="text"
                className="form-control"
                value={location}
                onChange={(e) =>
                  setLocation(e.target.value)
                }
              />

            </div>

            {/* DESCRIPTION */}
            <div className="mb-3">

              <label className="form-label">
                Description
              </label>

              <textarea
                className="form-control"
                rows="3"
                value={description}
                onChange={(e) =>
                  setDescription(e.target.value)
                }
              />

            </div>

            {/* MANTRA */}
            <div className="mb-3">

              <label className="form-label">
                Mantra
              </label>

              <input
                type="text"
                className="form-control"
                value={mantra}
                onChange={(e) =>
                  setMantra(e.target.value)
                }
              />

            </div>

            {/* ORDER */}
            <div className="mb-3">

              <label className="form-label">
                Order
              </label>

              <input
                type="number"
                className="form-control"
                value={order}
                onChange={(e) =>
                  setOrder(e.target.value)
                }
              />

            </div>

            {/* PUBLISHED */}
            <div className="form-check mb-3">

              <input
                className="form-check-input"
                type="checkbox"
                checked={isPublished}
                onChange={(e) =>
                  setIsPublished(e.target.checked)
                }
              />

              <label className="form-check-label">
                Published
              </label>

            </div>

            {/* ERROR */}
            {isError && (

              <div className="alert alert-danger">

                {error?.data?.message ||
                  "Update failed"}

              </div>

            )}

            {/* BUTTONS */}
            <div className="d-flex gap-2">

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isUpdating}
              >

                {isUpdating
                  ? "Updating..."
                  : "Update Event"}

              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >

                {isDeleting
                  ? "Deleting..."
                  : "Delete"}

              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  navigate("/dashboard/gallery-events")
                }
              >

                Cancel

              </button>

            </div>

          </form>

        </div>

      </div>

    </div>

  );

};

export default EditGalleryEvent;